import axios, {
    AxiosInstance, AxiosRequestConfig, AxiosResponse
} from 'axios';
import { supabase } from './authService/supabase';

// 扩展 Window 接口
declare global {
  interface Window {
    salerio?: {
      openJoinBetaModal: () => void;
      openSignInModal: () => void;
    };
  }
}

export interface ApiConfig {
  baseURL: string;
  headers?: Record<string, string>;
  debugMode?: boolean;
}

export interface ApiResponse<T> {
    data: T;
    code: string;
    message: string;
    status: string;
}

export class Api {
    private axiosInstance: AxiosInstance;

    constructor(apiConfig: ApiConfig) {
        this.axiosInstance = axios.create({
            baseURL: apiConfig.baseURL,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
                ...apiConfig.headers
            },
        });

        // 请求拦截器
        this.axiosInstance.interceptors.request.use(
            async (axiosConfig) => {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.access_token) {
                    axiosConfig.headers.Authorization = `Bearer ${session.access_token}`;
                }
                return axiosConfig;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // 响应拦截器
        this.axiosInstance.interceptors.response.use(
            (response) => {
                return response;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    }

    // 通用请求方法
    private async request<T>(config: AxiosRequestConfig): Promise<T> {
        try {
            const response: AxiosResponse<ApiResponse<T>> = await this.axiosInstance.request(config);
            if(response.data.code && response.data.code === '401') {
                throw new Error(response.data.code);
            }
            return response.data?.data || response?.data as T;
        } catch (error) {
            throw error;
        }
    }

    // HTTP 方法
    public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({
            ...config, method: 'GET', url 
        });
    }

    public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({
            ...config, method: 'POST', url, data 
        });
    }

    public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({
            ...config, method: 'PUT', url, data 
        });
    }

    public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({
            ...config, method: 'DELETE', url 
        });
    }
} 